(() => {
  if (window.ThemeCollectionFilters) {
    window.ThemeCollectionFilters.init(document);
    return;
  }

  const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
  const priceBucketAvailabilityCache = new Map();

  function collect(scope, selector) {
    const nodes = [];

    if (scope instanceof Element && scope.matches(selector)) {
      nodes.push(scope);
    }

    if (scope?.querySelectorAll) {
      nodes.push(...scope.querySelectorAll(selector));
    }

    return nodes;
  }

  function announce(message) {
    if (window.Theme?.announce) {
      window.Theme.announce(message);
    }
  }

  function hasSelectedSizeFilter(targetUrl) {
    return Array.from(targetUrl.searchParams.entries()).some(([name, value]) => (
      name.toLowerCase() === 'filter.v.option.size' && String(value).trim()
    ));
  }

  function isSizeFilterField(field) {
    return field instanceof HTMLInputElement
      && field.name.toLowerCase() === 'filter.v.option.size';
  }

  function clearAvailabilityWhenLastSizeIsUnchecked(form, changedField) {
    if (!(form instanceof HTMLFormElement)
      || !isSizeFilterField(changedField)
      || changedField.checked) {
      return;
    }

    const hasCheckedSize = collect(form, 'input[name="filter.v.option.size"]').some((field) => (
      field instanceof HTMLInputElement && field.checked
    ));

    if (hasCheckedSize) return;

    collect(form, 'input[name="filter.v.availability"]').forEach((field) => {
      if (field instanceof HTMLInputElement) field.checked = false;
    });
  }

  function enforceAvailableSizeFilter(urlInput) {
    const targetUrl = new URL(urlInput, window.location.origin);

    if (hasSelectedSizeFilter(targetUrl)) {
      targetUrl.searchParams.delete('filter.v.availability');
      targetUrl.searchParams.set('filter.v.availability', '1');
    }

    return targetUrl;
  }

  function normalizeLinkedSizeFilter(urlInput) {
    const targetUrl = enforceAvailableSizeFilter(urlInput);
    const currentUrl = new URL(window.location.href);

    if (hasSelectedSizeFilter(currentUrl) && !hasSelectedSizeFilter(targetUrl)) {
      targetUrl.searchParams.delete('filter.v.availability');
    }

    return targetUrl;
  }

  function buildFormUrl(form) {
    const targetUrl = new URL(form?.action || window.location.href, window.location.origin);
    const formData = new FormData(form);

    targetUrl.search = '';

    formData.forEach((value, name) => {
      if (name === '__price_bucket') return;

      const normalizedValue = String(value ?? '').trim();

      if (normalizedValue) {
        targetUrl.searchParams.append(name, normalizedValue);
      }
    });

    return enforceAvailableSizeFilter(targetUrl).toString();
  }

  function normalizePriceValue(value) {
    const normalizedValue = String(value ?? '').trim();

    if (!normalizedValue) return '';

    const numericValue = Number(normalizedValue);
    return Number.isFinite(numericValue) ? numericValue : normalizedValue;
  }

  function priceValuesMatch(firstValue, secondValue) {
    return normalizePriceValue(firstValue) === normalizePriceValue(secondValue);
  }

  function syncPriceBucket(group, selectedBucket) {
    if (!group || !(selectedBucket instanceof HTMLInputElement)) return;

    const minimumField = group.querySelector('[data-price-min]');
    const maximumField = group.querySelector('[data-price-max]');

    if (!(minimumField instanceof HTMLInputElement) || !(maximumField instanceof HTMLInputElement)) return;

    collect(group, '[data-price-bucket]').forEach((bucket) => {
      if (bucket !== selectedBucket && bucket instanceof HTMLInputElement) {
        bucket.checked = false;
      }
    });

    minimumField.value = selectedBucket.checked ? selectedBucket.dataset.priceMin || '' : '';
    maximumField.value = selectedBucket.checked ? selectedBucket.dataset.priceMax || '' : '';
  }

  function initPriceBuckets(scope = document) {
    collect(scope, '[data-price-buckets]').forEach((group) => {
      const minimumField = group.querySelector('[data-price-min]');
      const maximumField = group.querySelector('[data-price-max]');

      if (!(minimumField instanceof HTMLInputElement) || !(maximumField instanceof HTMLInputElement)) return;

      collect(group, '[data-price-bucket]').forEach((bucket) => {
        if (!(bucket instanceof HTMLInputElement)) return;

        bucket.checked = priceValuesMatch(bucket.dataset.priceMin || '', minimumField.value)
          && priceValuesMatch(bucket.dataset.priceMax || '', maximumField.value)
          && Boolean(minimumField.value || maximumField.value);
      });
    });
  }

  function buildPriceBucketUrl(group, bucket) {
    const form = group?.closest('[data-collection-filters-form]');
    const minimumField = group?.querySelector('[data-price-min]');
    const maximumField = group?.querySelector('[data-price-max]');

    if (!(form instanceof HTMLFormElement)
      || !(minimumField instanceof HTMLInputElement)
      || !(maximumField instanceof HTMLInputElement)
      || !(bucket instanceof HTMLInputElement)) {
      return '';
    }

    const targetUrl = new URL(buildFormUrl(form), window.location.origin);

    targetUrl.searchParams.delete(minimumField.name);
    targetUrl.searchParams.delete(maximumField.name);

    if (bucket.dataset.priceMin) {
      targetUrl.searchParams.set(minimumField.name, bucket.dataset.priceMin);
    }

    if (bucket.dataset.priceMax) {
      targetUrl.searchParams.set(maximumField.name, bucket.dataset.priceMax);
    }

    return targetUrl.toString();
  }

  async function getPriceBucketResultCount(targetUrl, sectionId) {
    const cacheKey = `${sectionId}:${targetUrl}`;

    if (!priceBucketAvailabilityCache.has(cacheKey)) {
      const request = fetchSection(targetUrl, sectionId).then((markup) => {
        const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
        const matchingSection = collect(parsedDocument, '[data-collection-section-id]').find((candidate) => (
          candidate.dataset.collectionSectionId === sectionId
        ));
        const countText = matchingSection?.querySelector('[data-collection-results-count]')?.textContent || '';
        const normalizedCount = Number(countText.replace(/[^0-9]/g, ''));

        return Number.isFinite(normalizedCount) ? normalizedCount : null;
      }).catch((error) => {
        priceBucketAvailabilityCache.delete(cacheKey);
        throw error;
      });

      priceBucketAvailabilityCache.set(cacheKey, request);
    }

    return priceBucketAvailabilityCache.get(cacheKey);
  }

  async function updatePriceBucketAvailability(section) {
    const sectionId = String(section?.dataset?.collectionSectionId || '').trim();
    const groups = collect(section, '[data-price-buckets]');
    const referenceGroup = groups.find((group) => !group.closest('dialog')) || groups[0];
    const referenceBuckets = referenceGroup ? collect(referenceGroup, '[data-price-bucket]') : [];

    if (!sectionId || !referenceGroup || !referenceBuckets.length) return;

    groups.forEach((group) => {
      group.dataset.priceAvailability = 'loading';
      group.setAttribute('aria-busy', 'true');

      collect(group, '[data-price-bucket]').forEach((bucket) => {
        if (bucket instanceof HTMLInputElement && !bucket.checked) bucket.disabled = true;
      });
    });

    const availability = await Promise.all(referenceBuckets.map(async (bucket) => {
      const targetUrl = buildPriceBucketUrl(referenceGroup, bucket);
      const count = targetUrl ? await getPriceBucketResultCount(targetUrl, sectionId) : null;

      return { count, value: bucket.value };
    }));

    if (!section.isConnected) return;

    groups.forEach((group) => {
      availability.forEach(({ count, value }) => {
        const matchingBucket = collect(group, '[data-price-bucket]').find((bucket) => bucket.value === value);

        if (!(matchingBucket instanceof HTMLInputElement)) return;

        const option = matchingBucket.closest('.facets__item');
        const isUnavailable = count === 0 && !matchingBucket.checked;

        matchingBucket.disabled = isUnavailable;
        matchingBucket.dataset.resultCount = count == null ? '' : String(count);

        if (option) option.hidden = isUnavailable;
      });

      group.dataset.priceAvailability = 'ready';
      group.removeAttribute('aria-busy');
    });

    initSidebarScrollbars(section);
  }

  function getFacetState(section) {
    const openFacetKeys = new Set();
    const expandedFacetKeys = new Set();

    collect(section, '[data-facet-key]').forEach((facet) => {
      const key = String(facet.dataset.facetKey || '').trim();

      if (!key) return;
      if (facet.open) openFacetKeys.add(key);

      if (facet.querySelector('[data-facet-values][data-expanded="true"]')) {
        expandedFacetKeys.add(key);
      }
    });

    return { expandedFacetKeys, openFacetKeys };
  }

  function restoreFacetState(section, { expandedFacetKeys, openFacetKeys } = {}) {
    collect(section, '[data-facet-key]').forEach((facet) => {
      const key = String(facet.dataset.facetKey || '').trim();

      if (!key) return;

      if (openFacetKeys?.has(key)) {
        facet.open = true;
      }

      if (expandedFacetKeys?.has(key)) {
        const values = facet.querySelector('[data-facet-values]');
        const toggle = facet.querySelector('[data-facets-show-more]');

        if (values) values.dataset.expanded = 'true';

        if (toggle) {
          toggle.setAttribute('aria-expanded', 'true');
          toggle.textContent = toggle.dataset.expandedLabel || 'Show less';
        }
      }
    });
  }

  function initSidebarScrollbars(scope = document) {
    collect(scope, '[data-facets-sidebar-scroll]').forEach((scrollContainer) => {
      const viewport = scrollContainer.querySelector('[data-facets-sidebar-scroll-viewport]');
      const indicator = scrollContainer.querySelector('[data-facets-sidebar-scrollbar-indicator]');

      if (!viewport || !indicator) return;

      const update = () => {
        const viewportHeight = viewport.clientHeight;
        const contentHeight = viewport.scrollHeight;
        const isScrollable = contentHeight > viewportHeight + 1;

        scrollContainer.classList.toggle('is-scrollable', isScrollable);

        if (!isScrollable) {
          indicator.style.height = '100%';
          indicator.style.transform = 'translate3d(0, 0, 0)';
          return;
        }

        const indicatorHeight = Math.max(24, viewportHeight * (viewportHeight / contentHeight));
        const maximumOffset = Math.max(0, viewportHeight - indicatorHeight);
        const maximumScroll = Math.max(1, contentHeight - viewportHeight);
        const indicatorOffset = maximumOffset * (viewport.scrollTop / maximumScroll);

        indicator.style.height = `${indicatorHeight}px`;
        indicator.style.transform = `translate3d(0, ${indicatorOffset}px, 0)`;
      };

      scrollContainer.__updateSidebarScrollbar = update;

      if (scrollContainer.dataset.sidebarScrollInitialized !== 'true') {
        scrollContainer.dataset.sidebarScrollInitialized = 'true';
        viewport.addEventListener('scroll', update, { passive: true });

        if ('ResizeObserver' in window) {
          const observer = new ResizeObserver(update);
          observer.observe(viewport);
          scrollContainer.__sidebarScrollResizeObserver = observer;
        }

        if ('MutationObserver' in window) {
          const observer = new MutationObserver(() => window.requestAnimationFrame(update));
          observer.observe(viewport, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ['data-expanded', 'open'],
          });
          scrollContainer.__sidebarScrollMutationObserver = observer;
        }
      }

      update();
    });
  }

  function focusControl(section, focusDescriptor = {}) {
    let focusTarget = null;

    if (focusDescriptor.type === 'field') {
      const matchingFields = collect(section, '[name]').filter((field) => (
        field.name === focusDescriptor.name
        && String(field.value) === focusDescriptor.value
        && Boolean(field.closest('dialog')) === Boolean(focusDescriptor.drawer)
      ));

      focusTarget = matchingFields[0] || null;
    } else if (focusDescriptor.type === 'sort') {
      focusTarget = collect(section, '[data-collection-sort-trigger]')
        .find((trigger) => trigger.getClientRects().length > 0);
    } else if (focusDescriptor.type === 'filter-trigger') {
      focusTarget = section.querySelector('[data-filter-open]');
    }

    focusTarget ||= section.querySelector('[data-collection-results]');

    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus({ preventScroll: true });
    }
  }

  function syncSortMenus(urlInput = window.location.href) {
    const currentUrl = new URL(urlInput, window.location.origin);
    const sortValue = currentUrl.searchParams.get('sort_by');

    collect(document, '[data-collection-sort-menu]').forEach((menu) => {
      const options = collect(menu, '[data-collection-sort-option]');
      const selectedOption = options.find((option) => option.dataset.sortValue === sortValue)
        || options.find((option) => option.hasAttribute('aria-current'))
        || options[0];

      if (!selectedOption) return;

      const currentLabel = menu.querySelector('[data-collection-sort-current]');

      if (currentLabel) {
        currentLabel.textContent = selectedOption.dataset.sortLabel || selectedOption.textContent.trim();
      }

      options.forEach((option) => {
        const isActive = option === selectedOption;

        option.classList.toggle('is-active', isActive);
        option.toggleAttribute('aria-current', isActive);
      });
    });
  }

  function initSortMenuInteractions() {
    if (document.documentElement.dataset.collectionSortMenuInitialized === 'true') return;

    document.documentElement.dataset.collectionSortMenuInitialized = 'true';

    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const sortOption = event.target.closest('[data-collection-sort-option]');

      if (!sortOption) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const targetUrl = enforceAvailableSizeFilter(window.location.href);

      targetUrl.searchParams.set('sort_by', sortOption.dataset.sortValue || '');
      sortOption.closest('[data-collection-sort-menu]')?.removeAttribute('open');

      const section = document.querySelector('[data-collection-section-id]');

      if (section?.dataset.collectionAjax !== 'true') {
        sortOption.href = targetUrl.toString();
        return;
      }

      event.preventDefault();

      replaceSection(section, targetUrl.toString(), {
        focusDescriptor: { type: 'sort' },
      }).then(() => {
        syncSortMenus(targetUrl);
        collect(document, '[data-collection-sort-trigger]')
          .find((trigger) => trigger.getClientRects().length > 0)
          ?.focus({ preventScroll: true });
      }).catch(() => {
        window.location.assign(targetUrl.toString());
      });
    });
  }

  async function fetchSection(targetUrl, sectionId) {
    const requestUrl = new URL(targetUrl, window.location.origin);

    requestUrl.searchParams.set('section_id', sectionId);

    const response = await fetch(requestUrl.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to update collection products.');
    }

    return response.text();
  }

  async function loadMoreProducts(section, button) {
    const sectionId = String(section?.dataset?.collectionSectionId || '').trim();
    const grid = section?.querySelector('.collection__grid');
    const currentPagination = button?.closest('[data-collection-pagination]');
    const targetUrl = button?.href;

    if (!sectionId || !grid || !currentPagination || !targetUrl) {
      throw new Error('Unable to load more products.');
    }

    const label = button.querySelector('[data-load-more-label]');
    const originalLabel = label?.textContent || '';

    button.dataset.loading = 'true';
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');

    if (label) {
      label.textContent = button.dataset.loadingLabel || 'Loading…';
    }

    try {
      let nextUrl = targetUrl;
      let incomingItems = [];
      let incomingPagination = null;
      const visited = new Set();
      // A Shopify result page may contain no variants matching the selected
      // in-stock size. Continue to its next page instead of showing an error.
      while (nextUrl && !incomingItems.length) {
        if (visited.has(nextUrl)) throw new Error('Repeated collection page.');
        visited.add(nextUrl);
        const markup = await fetchSection(nextUrl, sectionId);
        const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
        const nextSection = collect(parsedDocument, '[data-collection-section-id]').find((candidate) => (
          candidate.dataset.collectionSectionId === sectionId
        ));
        if (!nextSection) throw new Error('Unable to load more products.');
        incomingItems = collect(nextSection, '.collection__grid-item');
        incomingPagination = nextSection.querySelector('[data-collection-pagination]');
        nextUrl = incomingPagination?.querySelector('[data-collection-load-more]')?.href;
      }

      incomingItems.forEach((item) => grid.append(item));
      if (grid.querySelector('.collection__grid-item')) section.querySelector('[data-size-stock-empty]')?.remove();

      if (incomingPagination) {
        currentPagination.replaceWith(incomingPagination);
      } else {
        currentPagination.remove();
      }

      window.Theme?.init?.(grid);
      grid.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId },
      }));

      const loadedProductCount = grid.querySelectorAll('.collection__grid-item').length;
      announce(`${loadedProductCount} products loaded.`);
    } catch (error) {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      delete button.dataset.loading;

      if (label) label.textContent = originalLabel;

      throw error;
    }
  }

  async function replaceSection(currentSection, targetUrl, {
    focusDescriptor = {},
    historyMode = 'push',
    scrollToResults = false,
  } = {}) {
    const sectionId = String(currentSection?.dataset?.collectionSectionId || '').trim();

    if (!currentSection || !sectionId || !targetUrl) {
      throw new Error('Unable to update collection products.');
    }

    const requestId = Number(currentSection.__collectionRequestId || 0) + 1;
    const facetState = getFacetState(currentSection);

    currentSection.__collectionRequestId = requestId;
    currentSection.dataset.loading = 'true';
    currentSection.setAttribute('aria-busy', 'true');

    const markup = await fetchSection(targetUrl, sectionId);

    if (currentSection.__collectionRequestId !== requestId || !currentSection.isConnected) {
      return null;
    }

    const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
    const replacement = collect(parsedDocument, '[data-collection-section-id]').find((candidate) => (
      candidate.dataset.collectionSectionId === sectionId
    ));

    if (!replacement) {
      throw new Error('Unable to update collection products.');
    }

    replacement.classList.add('is-visible');
    replacement.dataset.animateInitialized = 'true';

    collect(currentSection, 'dialog[open]').forEach((dialog) => {
      dialog.close();
    });

    restoreFacetState(replacement, facetState);
    currentSection.replaceWith(replacement);

    if (historyMode === 'push') {
      window.history.pushState({ ...window.history.state, collectionFilters: true }, '', targetUrl);
    } else if (historyMode === 'replace') {
      window.history.replaceState({ ...window.history.state, collectionFilters: true }, '', targetUrl);
    }

    window.Theme?.init?.(replacement);
    init(replacement);
    replacement.dispatchEvent(new CustomEvent('shopify:section:load', {
      bubbles: true,
      detail: { sectionId },
    }));
    replacement.removeAttribute('aria-busy');
    delete replacement.dataset.loading;

    if (scrollToResults) {
      const toolbar = replacement.querySelector('[data-collection-toolbar]') || replacement;
      const top = toolbar.getBoundingClientRect().top + window.scrollY - 24;

      window.scrollTo({
        behavior: reducedMotionMedia.matches ? 'auto' : 'smooth',
        top,
      });
    }

    focusControl(replacement, focusDescriptor);

    const countLabel = replacement.querySelector('[data-collection-results-count]')?.textContent?.trim();
    announce(countLabel ? `${countLabel} loaded.` : 'Products updated.');

    return replacement;
  }

  function init(scope = document) {
    collect(scope, '[data-collection-section-id]').forEach((section) => {
      if (section.dataset.collectionInitialized === 'true') return;
      section.dataset.collectionInitialized = 'true';

      const currentUrl = new URL(window.location.href);
      const normalizedCurrentUrl = enforceAvailableSizeFilter(currentUrl);

      if (hasSelectedSizeFilter(currentUrl) && normalizedCurrentUrl.toString() !== currentUrl.toString()) {
        replaceSection(section, normalizedCurrentUrl.toString(), {
          historyMode: 'replace',
        }).catch(() => {
          window.location.assign(normalizedCurrentUrl.toString());
        });
        return;
      }

      initPriceBuckets(section);
      initSidebarScrollbars(section);
      updatePriceBucketAvailability(section).catch(() => {
        collect(section, '[data-price-buckets]').forEach((group) => {
          group.dataset.priceAvailability = 'error';
          group.removeAttribute('aria-busy');

          collect(group, '[data-price-bucket]').forEach((bucket) => {
            if (bucket instanceof HTMLInputElement) bucket.disabled = false;
          });
        });
      });

      let automaticSubmitTimer = 0;

      section.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) return;

        const filterOpenButton = event.target.closest('[data-filter-open]');

        if (filterOpenButton && section.contains(filterOpenButton)) {
          window.requestAnimationFrame(() => initSidebarScrollbars(section));
        }

        const showMoreButton = event.target.closest('[data-facets-show-more]');

        if (showMoreButton && section.contains(showMoreButton)) {
          const facet = showMoreButton.closest('[data-facet-key]');
          const values = facet?.querySelector('[data-facet-values]');
          const isExpanded = values?.dataset.expanded === 'true';

          if (!values) return;

          values.dataset.expanded = isExpanded ? 'false' : 'true';
          showMoreButton.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
          showMoreButton.textContent = isExpanded
            ? showMoreButton.dataset.collapsedLabel || 'Show more'
            : showMoreButton.dataset.expandedLabel || 'Show less';
          window.requestAnimationFrame(() => initSidebarScrollbars(section));
          return;
        }

        const loadMoreButton = event.target.closest('[data-collection-load-more]');

        if (loadMoreButton && section.contains(loadMoreButton)) {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

          event.preventDefault();

          if (loadMoreButton.dataset.loading === 'true') return;

          const targetUrl = loadMoreButton.href;

          loadMoreProducts(section, loadMoreButton).catch(() => {
            window.location.assign(targetUrl);
          });
          return;
        }

        const collectionLink = event.target.closest('[data-collection-filter-link], .collection__pagination a');

        if (!collectionLink || !section.contains(collectionLink)) return;
        if (section.dataset.collectionAjax !== 'true') return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const targetUrl = normalizeLinkedSizeFilter(collectionLink.href);

        if (targetUrl.origin !== window.location.origin) return;

        event.preventDefault();

        replaceSection(section, targetUrl.toString(), {
          focusDescriptor: { type: 'filter-trigger' },
          scrollToResults: Boolean(collectionLink.closest('.collection__pagination')),
        }).catch(() => {
          window.location.assign(targetUrl.toString());
        });
      });

      section.addEventListener('change', (event) => {
        if (!(event.target instanceof Element)) return;

        const sortField = event.target.closest('[data-collection-sort]');

        if (sortField?.form) {
          sortField.form.requestSubmit();
          return;
        }

        const filterForm = event.target.closest('[data-collection-filters-form]');

        if (!filterForm) return;

        clearAvailabilityWhenLastSizeIsUnchecked(filterForm, event.target);

        const priceBucket = event.target.closest('[data-price-bucket]');

        if (priceBucket instanceof HTMLInputElement) {
          syncPriceBucket(priceBucket.closest('[data-price-buckets]'), priceBucket);
        }

        filterForm.__collectionFocusField = event.target;

        if (filterForm.dataset.autoApply !== 'true' || filterForm.closest('dialog')) return;

        window.clearTimeout(automaticSubmitTimer);
        automaticSubmitTimer = window.setTimeout(() => {
          if (filterForm.isConnected) filterForm.requestSubmit();
        }, 180);
      });

      section.addEventListener('submit', (event) => {
        const form = event.target;

        if (!(form instanceof HTMLFormElement)) return;
        if (!form.matches('[data-collection-filters-form], [data-collection-sort-form]')) return;
        if (section.dataset.collectionAjax !== 'true') return;

        event.preventDefault();

        const targetUrl = buildFormUrl(form);
        const isSortForm = form.matches('[data-collection-sort-form]');
        const isDrawerForm = Boolean(form.closest('dialog'));
        const focusField = form.__collectionFocusField;
        let focusDescriptor = { type: 'filter-trigger' };

        if (isSortForm) {
          focusDescriptor = { type: 'sort' };
        } else if (!isDrawerForm && focusField) {
          focusDescriptor = {
            drawer: false,
            name: focusField.name,
            type: 'field',
            value: String(focusField.value),
          };
        }

        replaceSection(section, targetUrl, { focusDescriptor }).catch(() => {
          window.location.assign(targetUrl);
        });
      });
    });
  }

  window.ThemeCollectionFilters = {
    init,
    replace: replaceSection,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initSortMenuInteractions();
      syncSortMenus();
      init(document);
    }, { once: true });
  } else {
    initSortMenuInteractions();
    syncSortMenus();
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
  });

  window.addEventListener('popstate', () => {
    const section = document.querySelector('[data-collection-section-id]');

    if (section?.dataset.collectionAjax !== 'true') return;

    replaceSection(section, window.location.href, {
      historyMode: 'none',
    }).then(() => {
      syncSortMenus();
    }).catch(() => {
      window.location.reload();
    });
  });
})();
